/**
 * ADA Job Requirements & Opportunities Manager
 * CLIENT-SIDE ONLY - Fetches jobs from GitHub or local fallback JSON
 * No backend server required
 */
import { fetchDataWithFallback, saveDataToGitHub } from '../config/dataSources';
import { normalizeJob } from '../services/clientJobCrawler';

const JOBS_CACHE_KEY = 'nibyor_jobs_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

// In-memory cache for jobs
let jobsCache = {
  data: [],
  timestamp: 0,
  isLoading: false
};

// Clear jobs cache (use after saving new jobs to force reload)
export const clearJobsCache = () => {
  jobsCache.data = [];
  jobsCache.timestamp = 0;
  jobsCache.isLoading = false;
};

/**
 * Fetch jobs from GitHub or local fallback
 * CLIENT-SIDE ONLY - No API calls
 * @param {Object} filters - Filter options {industry, level, search}
 * @returns {Promise<Array>} Jobs array
 */
export const getAllADAJobs = async (filters = {}) => {
  try {
    // Check if we have valid cached data
    const now = Date.now();
    if (jobsCache.data.length > 0 && (now - jobsCache.timestamp) < CACHE_DURATION) {
      console.log('📦 Using cached jobs data');
      return applyFilters(jobsCache.data, filters);
    }

    // Return cached data if loading to prevent duplicate requests
    if (jobsCache.isLoading && jobsCache.data.length > 0) {
      console.log('⏳ Using cached jobs while fetching updates');
      return applyFilters(jobsCache.data, filters);
    }

    jobsCache.isLoading = true;

    // Fetch from GitHub or local fallback (CLIENT-SIDE)
    console.log('🔍 Fetching jobs from GitHub/local storage...');
    const data = await fetchDataWithFallback('jobs', { timeout: 10000 });

    jobsCache.isLoading = false;

    if (!data) {
      console.warn('⚠️ No jobs data available');
      return [];
    }

    // Handle different data formats
    let jobs = [];
    if (Array.isArray(data.jobs)) {
      jobs = data.jobs;
    } else if (Array.isArray(data)) {
      jobs = data;
    }

    // Normalize every job so both crawler & consumer field names exist
    jobs = jobs.map(normalizeJob);

    // Update cache
    jobsCache.data = jobs;
    jobsCache.timestamp = now;

    console.log(`✅ Loaded ${jobs.length} jobs (client-side)`);
    return applyFilters(jobs, filters);

  } catch (error) {
    console.error('❌ Error fetching jobs:', error);
    jobsCache.isLoading = false;
    
    // Return cached data if available
    if (jobsCache.data.length > 0) {
      console.warn('⚠️ Using cached data due to fetch error');
      return applyFilters(jobsCache.data, filters);
    }
    
    return [];
  }
};

/**
 * Apply client-side filters to jobs
 */
const applyFilters = (jobs, filters) => {
  let filtered = [...jobs];

  if (filters.industry) {
    filtered = filtered.filter(j => 
      (j.industry || '').toLowerCase().includes(filters.industry.toLowerCase())
    );
  }

  if (filters.level) {
    filtered = filtered.filter(j => (j.level || j.experience || '') === filters.level);
  }

  if (filters.search) {
    const term = filters.search.toLowerCase();
    filtered = filtered.filter(j =>
      (j.jobTitle || j.title || '').toLowerCase().includes(term) ||
      (j.company || '').toLowerCase().includes(term) ||
      (j.description || '').toLowerCase().includes(term)
    );
  }

  return filtered.sort((a, b) => {
    const dateA = a.postedDate || a.posted_date || '';
    const dateB = b.postedDate || b.posted_date || '';
    return new Date(dateB) - new Date(dateA);
  });
};

/**
 * Get specific job details
 * CLIENT-SIDE ONLY - Fetches from cache/GitHub
 * @param {string} jobId - Job ID
 * @returns {Promise<Object>} Job details
 */
export const getADAJobRequirements = async (jobId) => {
  try {
    // Get all jobs from cache or GitHub
    const allJobs = await getAllADAJobs();
    
    // Find the specific job
    const job = allJobs.find(j => j.id === jobId || j._id === jobId);
    
    if (!job) {
      throw new Error(`Job not found: ${jobId}`);
    }

    console.log('✅ Loaded job details:', jobId);
    return job;

  } catch (error) {
    console.error('❌ Error fetching job details:', error);
    throw error;
  }
};

/**
 * Trigger job crawling (admin function)
 * NOTE: CLIENT-SIDE ONLY - No backend crawling available
 * Jobs are updated via GitHub repository directly
 * @deprecated Use GitHub Actions or manual updates to populate jobs data
 * @returns {Promise<Object>} Info message
 */
export const triggerJobCrawl = async (options = {}) => {
  console.warn('⚠️ triggerJobCrawl is not available in client-side mode');
  if (options && Object.keys(options).length) {
    console.info('Crawl options ignored (client-side mode):', options);
  }
  console.info('ℹ️ Jobs are fetched from GitHub repository directly');
  console.info('ℹ️ Update jobs via GitHub Actions or manual repository updates');
  
  // Clear cache to trigger a fresh fetch from GitHub
  jobsCache.timestamp = 0;
  
  return {
    success: false,
    message: 'Client-side mode: Manual crawling not available. Jobs fetched from GitHub.',
    mode: 'client-side',
    action: 'Update jobs.json in GitHub repository to refresh data'
  };
};

/**
 * Get job data status from GitHub/cache
 * CLIENT-SIDE ONLY - No backend status endpoint
 * @returns {Promise<Object>} Job data status info
 */
export const getCrawlStatus = async () => {
  try {
    // Use cached data if available
    if (jobsCache.data.length > 0) {
      return {
        totalJobsInDatabase: jobsCache.data.length,
        lastUpdated: new Date(jobsCache.timestamp).toISOString(),
        dataSource: 'GitHub/Local (cached)',
        mode: 'client-side'
      };
    }

    // Fetch fresh data if no cache
    const data = await fetchDataWithFallback('jobs', { timeout: 5000 });
    let jobs = [];
    
    if (Array.isArray(data?.jobs)) {
      jobs = data.jobs;
    } else if (Array.isArray(data)) {
      jobs = data;
    }

    return {
      totalJobsInDatabase: jobs.length,
      lastUpdated: new Date().toISOString(),
      dataSource: 'GitHub/Local',
      mode: 'client-side'
    };

  } catch (error) {
    console.error('❌ Error fetching job status:', error);
    return {
      totalJobsInDatabase: 0,
      lastUpdated: new Date().toISOString(),
      dataSource: 'unavailable',
      mode: 'client-side',
      error: error.message
    };
  }
};

/**
 * Get job statistics from cached/GitHub data
 * CLIENT-SIDE ONLY - Computes stats from available data
 * @returns {Promise<Object>} Job statistics
 */
export const getJobStats = async () => {
  try {
    // Get all jobs from cache or GitHub
    const allJobs = await getAllADAJobs();
    
    // Compute statistics
    const stats = {
      total: allJobs.length,
      active: allJobs.filter(j => (j.status || 'active') === 'active').length,
      byIndustry: {},
      byLevel: {},
      recentlyAdded: allJobs.filter(j => {
        const daysSincePosted = (Date.now() - new Date(j.postedDate || j.posted_date)) / (1000 * 60 * 60 * 24);
        return daysSincePosted <= 7;
      }).length
    };
    
    // Count by industry
    allJobs.forEach(job => {
      const industry = job.industry || 'Other';
      stats.byIndustry[industry] = (stats.byIndustry[industry] || 0) + 1;
    });
    
    // Count by level
    allJobs.forEach(job => {
      const level = job.level || job.experience || 'Other';
      stats.byLevel[level] = (stats.byLevel[level] || 0) + 1;
    });
    
    return stats;

  } catch (error) {
    console.error('❌ Error computing stats:', error);
    return {
      total: 0,
      active: 0,
      byIndustry: {},
      byLevel: {},
      recentlyAdded: 0,
      error: error.message
    };
  }
};

/**
 * Track job seeker interest in a specific job
 * @param {string} seekerId - Job seeker ID
 * @param {string} jobId - Job ID
 * @param {string} status - Status (interested, applied, rejected, etc.)
 */
export const trackJobSeekerInterest = (seekerId, jobId, status = 'interested') => {
  const trackingData = {
    seekerId,
    jobId,
    status,
    trackedAt: new Date().toISOString(),
    updateHistory: [
      {
        status,
        timestamp: new Date().toISOString()
      }
    ]
  };

  // Store in localStorage for persistence
  const key = `job_tracking_${seekerId}_${jobId}`;
  localStorage.setItem(key, JSON.stringify(trackingData));

  return trackingData;
};

/**
 * Update job seeker tracking status
 * @param {string} seekerId - Job seeker ID
 * @param {string} jobId - Job ID
 * @param {string} newStatus - New status
 */
export const updateJobSeekerTracking = (seekerId, jobId, newStatus) => {
  const key = `job_tracking_${seekerId}_${jobId}`;
  const stored = localStorage.getItem(key);
  let tracking = stored ? JSON.parse(stored) : null;

  if (!tracking) {
    return trackJobSeekerInterest(seekerId, jobId, newStatus);
  }

  tracking.status = newStatus;
  tracking.updateHistory.push({
    status: newStatus,
    timestamp: new Date().toISOString()
  });
  tracking.lastUpdated = new Date().toISOString();

  localStorage.setItem(key, JSON.stringify(tracking));
  return tracking;
};

/**
 * Get job seeker's tracked jobs
 * @param {string} seekerId - Job seeker ID
 * @returns {Array} Tracked jobs with status
 */
export const getJobSeekerTracking = (seekerId) => {
  const tracking = [];
  
  // Retrieve all tracking entries from localStorage
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(`job_tracking_${seekerId}`)) {
      const data = JSON.parse(localStorage.getItem(key));
      tracking.push(data);
    }
  }

  return tracking;
};

/**
 * Export jobs to CSV for admin monitoring
 * @returns {string} CSV formatted data
 */
export const exportJobsToCSV = async () => {
  try {
    const jobs = await getAllADAJobs();
    
    const headers = [
      'Job ID', 'Job Title', 'Company', 'Location', 'Industry', 'Level',
      'Employment Type', 'Salary', 'Description', 'Skills',
      'ADA Requirements', 'Contact Email', 'Contact Phone', 'Company Website',
      'Application URL', 'Posted Date', 'Source'
    ];
    
    const escCSV = (val) => {
      if (val == null) return '';
      const str = Array.isArray(val) ? val.join('; ') : String(val);
      return `"${str.replace(/"/g, '""')}"`;
    };
    
    const rows = jobs.map(job => [
      job.id || '',
      job.title || job.jobTitle || '',
      job.company || '',
      job.location || '',
      job.industry || '',
      job.experience || job.level || '',
      job.type || job.employmentType || 'Full-time',
      job.salary || '',
      job.description || '',
      (job.skills || []).join('; '),
      (job.adaRequirements || []).join('; '),
      job.contactEmail || '',
      job.contactPhone || '',
      job.company_website || job.companyWebsite || '',
      job.apply_url || job.applicationUrl || '',
      job.posted_date || job.postedDate || new Date().toISOString().split('T')[0],
      job.source || ''
    ].map(escCSV));

    const csv = [
      headers.map(h => escCSV(h)).join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    return csv;
  } catch (error) {
    console.error('Error exporting jobs:', error);
    return '';
  }
};

/**
 * Get daily stats for admin dashboard
 * @returns {Object} Daily statistics
 */
export const getDailyADAJobStats = async () => {
  try {
    const jobs = await getAllADAJobs();
    const today = new Date().toISOString().split('T')[0];
    
    const todayJobs = jobs.filter(job => (job.postedDate || job.posted_date || '').startsWith(today));
    const industries = new Set(jobs.map(j => j.industry));
    const levels = new Set(jobs.map(j => j.level || j.experience));

    return {
      totalJobs: jobs.length,
      todayJobs: todayJobs.length,
      uniqueIndustries: industries.size,
      uniqueLevels: levels.size,
      lastUpdated: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error getting daily stats:', error);
    return { totalJobs: 0, todayJobs: 0, uniqueIndustries: 0, uniqueLevels: 0 };
  }
};

/**
 * Delete a job (admin only)
 * Not implemented on client side - use backend API
 */
export const deleteADAJob = (jobId) => {
  console.warn('Use backend API to delete jobs');
  return false;
};

/**
 * Archive a job (admin only)
 * Not implemented on client side - use backend API
 */
export const archiveADAJob = (jobId) => {
  console.warn('Use backend API to archive jobs');
  return false;
};

/**
 * Get analytics for admin dashboard
 * @returns {Object} Detailed analytics
 */
export const getADAJobAnalytics = async () => {
  try {
    const stats = await getJobStats();
    return stats || {};
  } catch (error) {
    console.error('Error getting analytics:', error);
    return {};
  }
};

// Re-export batch function for backward compatibility
export const generateBatchADAJobRequirements = async (jobs) => {
  console.warn('Batch generation now handled by backend crawl service');
  return [];
};

export default {
  getAllADAJobs,
  getADAJobRequirements,
  triggerJobCrawl,
  getCrawlStatus,
  getJobStats,
  trackJobSeekerInterest,
  updateJobSeekerTracking,
  getJobSeekerTracking,
  exportJobsToCSV,
  getDailyADAJobStats,
  deleteADAJob,
  archiveADAJob,
  getADAJobAnalytics,
  generateBatchADAJobRequirements
};
